import React, { useEffect, useState } from "react";
import { useClub } from "../club/ClubContext";
import { fetchMyPenalties } from "../dataProvider/resources/penalties";
import { PlayerPenalty } from "./types";
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Divider,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";

function formatPrice(n: number) {
  return `€ ${n.toFixed(2).replace(".", ",")}`;
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function MyPenaltiesPage() {
  const { clubId } = useClub();
  const [penalties, setPenalties] = useState<PlayerPenalty[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clubId) { setLoading(false); return; }
    fetchMyPenalties(clubId)
      .then(setPenalties)
      .catch(() => setPenalties([]))
      .finally(() => setLoading(false));
  }, [clubId]);

  const totalOpen = penalties.filter(p => p.status === "open").reduce((s, p) => s + p.amount, 0);
  const totalPaid = penalties.filter(p => p.status === "paid").reduce((s, p) => s + p.amount, 0);

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 680, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700} mb={2} display="flex" alignItems="center" gap={1}>
        <GavelIcon /> Meine Strafen
      </Typography>

      {penalties.length > 0 && (
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          {totalOpen > 0 && (
            <Card sx={{ flex: 1, minWidth: 140, borderLeft: "4px solid", borderLeftColor: "warning.main" }}>
              <CardContent sx={{ pb: "12px !important" }}>
                <Typography variant="caption" color="text.secondary">Offen</Typography>
                <Typography variant="h6" fontWeight={700} color="warning.main">{formatPrice(totalOpen)}</Typography>
              </CardContent>
            </Card>
          )}
          {totalPaid > 0 && (
            <Card sx={{ flex: 1, minWidth: 140, borderLeft: "4px solid", borderLeftColor: "success.main" }}>
              <CardContent sx={{ pb: "12px !important" }}>
                <Typography variant="caption" color="text.secondary">Bezahlt</Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">{formatPrice(totalPaid)}</Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {penalties.length === 0 ? (
        <Box textAlign="center" py={6}>
          <Typography color="text.secondary">Keine Strafen vorhanden.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {penalties.map(p => (
            <Card key={p.id} sx={{ borderLeft: "4px solid", borderLeftColor: p.status === "paid" ? "success.main" : "warning.main" }}>
              <CardContent sx={{ pb: "12px !important" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography fontWeight={600}>{p.penalty_type_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(p.created_at)}
                      {p.assigned_by_display_name && ` · von ${p.assigned_by_display_name}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                    <Typography fontWeight={700} color={p.status === "paid" ? "success.main" : "warning.main"}>
                      {formatPrice(p.amount)}
                    </Typography>
                    <Chip
                      label={p.status === "paid" ? "Bezahlt" : "Offen"}
                      color={p.status === "paid" ? "success" : "warning"}
                      size="small"
                    />
                  </Box>
                </Box>
                {p.note && (
                  <>
                    <Divider sx={{ my: 0.75 }} />
                    <Typography variant="body2" color="text.secondary">Anmerkung: {p.note}</Typography>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}
